import { useCallback, useEffect, useState } from 'react';
import { ApiClient, LocalStorageTokenStore } from '@rydres/api-client';
import { homeAddressOf, type PlaceInput, type SavedPlace } from '@rydres/shared';
import type { ChatApi, ChatMessage, ChatThreadData } from '@rydres/ui';
import { api, API_ORIGIN } from './api.js';
import type { PassengerDetails, PassengerMe, PublicBookingView } from './types.js';

/** The passenger's own account on this site, kept apart from the public calls. */
export const passengerApi = new ApiClient({ baseUrl: `${API_ORIGIN}/v1`, store: new LocalStorageTokenStore('rydres.passenger') });
let restored: Promise<unknown> | null = null;
/** The stored passenger session, read once per page load. */
export const passengerSession = () => (restored ??= passengerApi.init());

export interface RiderConversation {
  id: string;
  name: string;
  handle: string | null;
  photoUrl: string | null;
  closed: boolean;
  unread: number;
  last: { body: string; sender: 'driver' | 'passenger'; mine: boolean; at: string } | null;
}
export interface RiderConversations {
  conversations: RiderConversation[];
  unread: number;
  /** Texts about new messages: the passenger's choice, the wording, and whether they are being sent yet. */
  alerts: { on: boolean; wording: string; textsLive: boolean };
}

/** A ride on the passenger's Home; `statusUrl` is its page with the details and cancelling. */
export interface RiderRide {
  id: string;
  kind: string;
  startsAt: string;
  status: string;
  pickupAddress: string;
  dropoffAddress: string;
  priceCents: number | null;
  paymentMode: string;
  /** They sent this themselves; otherwise it only names their number, and they can say it is not theirs. */
  verified: boolean;
  driver: { name: string | null; handle: string | null; photoUrl: string | null; timezone: string };
  statusUrl: string;
  /** A price from the driver waiting for an answer: its page, where the passenger accepts and pays. */
  quoteUrl: string | null;
}

export const riderRides = (query?: { from?: string; to?: string; before?: string; limit?: number }) => passengerApi.call<{ rides: RiderRide[] }>('GET', '/passenger/rides', { query });

/** One ride's money in the rider's payment history. */
export interface RiderPayment {
  id: string;
  date: string;
  /** Its date where its driver works, YYYY-MM-DD. */
  day: string;
  pickupAddress: string;
  dropoffAddress: string;
  driver: { name: string | null; legalName: string | null; tcpNumber: string | null; handle: string | null; timezone: string };
  amountCents: number;
  refundedCents: number;
  paidCents: number;
  label: string;
  receiptUrl: string | null;
  manageToken: string;
}
export interface RiderPayments {
  records: RiderPayment[];
  totals: { paidCents: number; rides: number; byMonth: Array<{ key: string; paidCents: number; rides: number }>; byYear: Array<{ key: string; paidCents: number; rides: number }> };
}
export const riderPayments = (query: { from?: string; to?: string }) => passengerApi.call<RiderPayments>('GET', '/passenger/payments', { query });
/** A short-lived link to a statement of those payments. */
export const riderStatementLink = (body: { from?: string; to?: string; format: 'pdf' | 'csv' }) => passengerApi.call<{ url: string }>('POST', '/passenger/statements', { body });

/** The rider's answer on a backup driver offered to them. */
export const approveReferral = (id: string) => passengerApi.call<{ manageToken: string; quoteToken: string }>('POST', `/passenger/referrals/${id}/approve`);
export const refuseReferral = (id: string) => passengerApi.call<PublicBookingView>('POST', `/passenger/referrals/${id}/refuse`);

/** Asks a driver for a ride from the signed-in account. */
export const sendRideRequest = (body: Record<string, unknown>) => passengerApi.call<PublicBookingView>('POST', '/passenger/requests', { body });

/** This ride only named the rider's number and is not theirs. */
export const riderNotMine = (id: string) => passengerApi.call<{ rides: RiderRide[] }>('POST', `/passenger/rides/${id}/not-mine`);

export interface RiderNotification {
  id: string;
  title: string;
  body: string;
  url: string | null;
  at: string;
  read: boolean;
}

/** What is under the bell on Home. */
export const riderNotifications = {
  list: () => passengerApi.call<{ notifications: RiderNotification[]; unread: number }>('GET', '/passenger/notifications'),
  unread: () => passengerApi.call<{ unread: number }>('GET', '/passenger/notifications/unread'),
  read: () => passengerApi.call('POST', '/passenger/notifications/read'),
};

/** A notification names a screen of the phone app; this is the same place on this site. */
export function sitePath(url: string | null): string | null {
  if (!url) return null;
  const quote = /[?&]quote=([\w-]+)/.exec(url);
  if (quote) return `/q/${quote[1]}`;
  const ride = /^\/ride\/([\w-]+)/.exec(url);
  if (ride) return `/b/${ride[1]}`;
  const book = /^\/book\/([\w-]+)/.exec(url);
  return book ? `/me/request/${book[1]}` : null;
}

/** Asks the header and tabs to check the sign-in and unread messages again. */
export const riderChanged = () => window.dispatchEvent(new Event('rydres:unread'));

/**
 * For the header and the tabs: whether a passenger is signed in on this browser, and their unread
 * messages. Checked on every page change, after sign-in, sign-out and reading, and every 30 seconds
 * on the account pages.
 */
export function useRiderBar(pathname: string) {
  const [unread, setUnread] = useState<number | null>(null);
  const onAccount = pathname.startsWith('/me');
  useEffect(() => {
    let live = true;
    const load = () =>
      void passengerSession()
        .then(() => (passengerApi.currentSession ? riderChat.unread().then((r) => r.unread) : null))
        .then((n) => live && setUnread(n), () => undefined);
    load();
    window.addEventListener('rydres:unread', load);
    const timer = onAccount ? setInterval(() => document.visibilityState === 'visible' && load(), 30_000) : undefined;
    return () => {
      live = false;
      window.removeEventListener('rydres:unread', load);
      clearInterval(timer);
    };
  }, [pathname, onAccount]);
  return { signedIn: unread !== null, unread: unread ?? 0 };
}

/** The passenger's conversations with drivers. */
export const riderChat = {
  list: () => passengerApi.call<RiderConversations>('GET', '/passenger/conversations'),
  unread: () => passengerApi.call<{ unread: number }>('GET', '/passenger/conversations/unread'),
  open: (handle: string) => passengerApi.call<{ id: string }>('POST', '/passenger/conversations', { body: { handle } }),
  alerts: (on: boolean) => passengerApi.call<RiderConversations>('PUT', '/passenger/message-alerts', { body: { on } }),
};

export const passengerChat: ChatApi = {
  thread: (id, after) => passengerApi.call<ChatThreadData>('GET', `/passenger/conversations/${id}`, { query: after ? { after } : undefined }),
  send: (id, body) => passengerApi.call<ChatMessage>('POST', `/passenger/conversations/${id}/messages`, { body: { body } }),
  read: (id) => passengerApi.call('POST', `/passenger/conversations/${id}/read`),
  block: (id, on) => passengerApi.call<ChatThreadData>('POST', `/passenger/conversations/${id}/block`, { body: { on } }),
  report: (id, reason) => passengerApi.call('POST', `/passenger/conversations/${id}/report`, { body: { reason } }),
};

/** Whether passenger accounts are open, and the signed-in passenger, if any. */
export function usePassenger() {
  const [state, setState] = useState<{ loading: boolean; open: boolean; referrals: boolean; me: PassengerMe | null }>({ loading: true, open: false, referrals: false, me: null });
  const reload = useCallback(async () => {
    const [features] = await Promise.all([
      api.call<{ passengerAccounts: boolean; rideReferrals?: boolean }>('GET', '/public/features', { auth: false }).catch(() => ({ passengerAccounts: false, rideReferrals: false })),
      passengerSession(),
    ]);
    const me =
      features.passengerAccounts && passengerApi.currentSession
        ? await passengerApi.call<PassengerMe>('GET', '/passenger/me').catch(() => null)
        : null;
    setState({ loading: false, open: features.passengerAccounts, referrals: Boolean(features.rideReferrals), me });
    return me;
  }, []);
  useEffect(() => void reload(), [reload]);
  return {
    ...state,
    reload,
    save: async (details: PassengerDetails) => {
      const me = await passengerApi.call<PassengerMe>('PATCH', '/passenger/me', { body: details });
      setState((s) => ({ ...s, me }));
      return me;
    },
    savePlaces: async (places: PlaceInput[]) => {
      const r = await passengerApi.call<{ places: SavedPlace[] }>('PUT', '/passenger/places', { body: { places } });
      setState((s) => ({ ...s, me: s.me && { ...s.me, places: r.places, details: { ...s.me.details, homeAddress: homeAddressOf(r.places) } } }));
      return r.places;
    },
    share: async (handle: string, share: boolean) => {
      const me = await passengerApi.call<PassengerMe>('PUT', `/passenger/drivers/${encodeURIComponent(handle)}`, { body: { share } });
      setState((s) => ({ ...s, me }));
      return me;
    },
    signOut: async () => {
      await passengerApi.signOut();
      setState((s) => ({ ...s, me: null }));
      riderChanged();
    },
  };
}
